import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect';
import Show from '@/components/Molecules/Show';
import { useTourContext } from '@/store/tourContext';

enum TutorialDisplayName {
  OnboardingUser = 'Onboarding User Tutorial',
  BenefitVerificationTutorial = 'Benefit Verification Tutorial',
  OrderStatusTutorial = 'Order Status Tutorial',
  InvoicesTutorial = 'Invoices Tutorial',
  DashboardTutorial = 'Dashboard Tutorial',
  //SALES REP
  BenefitVerificationTutorialSalesRep = 'Benefit Verification Tutorial (Sales Rep)',
  ReturnsTutorialSalesRep = 'Returns Tutorial (Sales Rep)',
  InvoicesTutorialSalesRep = 'Invoices Tutorial (Sales Rep)',
  DashboardTutorialSalesRep = 'Dashboard Tutorial (Sales Rep)',
  OnboardingSalesRepTutorial = 'Onboarding Tutorial (Sales Rep)',

  //WC+
  BenefitVerificationTutorialWC = 'Benefit Verification Tutorial (WC+)',
  ReturnsTutorialWC = 'Returns Tutorial (WC+)',
  InvoicesTutorialWC = 'Invoices Tutorial (WC+)',
  OrderStatusTutorialWC = 'Order Status Tutorial (WC+)',
  DashboardTutorialWC = 'Dashboard Tutorial (WC+)',
  OnboardingWCTutorial = 'Onboarding Tutorial (WC+)',

  //Mindsight
  BenefitVerificationTutorialMindsight = 'Benefit Verification Tutorial (Mindsight)',
  OrderStatusTutorialMindsight = 'Order Status Tutorial (Mindsight)',
  ReturnsTutorialMindsight = 'Returns Tutorial (Mindsight)',
  InvoicesTutorialMindsight = 'Invoices Tutorial (Mindsight)',
  OnboardingMindsightTutorial = 'Onboarding Tutorial (Mindsight)',

  //GraftLogs
  OnboardingTutorialGraftLogs = 'Onboarding Tutorial (GraftLogs)',
  
  //Payments MSL
  OnboardingTutorialPaymentMSL = 'Onboarding Tutorial (Payments MSL)',
}

const Tutorials = () => {
  const { getAvailableTutorials, startTour, isTourActive } = useTourContext();
  const [selectedTutorial, setSelectedTutorial] = useState<string>('');

  const availableTutorials = getAvailableTutorials();
  const labeledTutorials = useMemo(
    () =>
      availableTutorials.map((tutorial) => ({
        value: tutorial.name,
        label:
          (TutorialDisplayName[tutorial.name as keyof typeof TutorialDisplayName] ??
            `${tutorial.name} Tutorial`) +
          (tutorial.enabled === false ? ' (Disabled)' : ''),
        disabled: tutorial.enabled === false,
      })),
    [availableTutorials]
  );

  const selectedTutorialData = useMemo(
    () => availableTutorials.find((t) => t.name === selectedTutorial),
    [availableTutorials, selectedTutorial],
  );
  const isTutorialDisabled = selectedTutorialData?.enabled === false;

  const handleStartTutorial = () => {
    if (!selectedTutorial) {
      return;
    }

    // Prevent starting disabled tutorials
    if (isTutorialDisabled) {
      return;
    }

    void startTour(selectedTutorial, true); // true indicates the tutorial was started manually
  };

  return (
    <Box
      w="100%"
      px={{ base: 4, md: 6, lg: 8 }}
      py={{ base: 4, md: 6 }}
    >
      <Heading 
        size="md" 
        mb={4}
        fontSize={{ base: "lg", md: "xl" }}
      >
        Tutorials
      </Heading>
      <Text 
        mb={6} 
        color="gray.600" 
        fontSize={{ base: "xs", md: "sm" }}
      >
        Select a tutorial from the list below to start an interactive guide
        through the application features.
      </Text>

      <Flex 
        direction="column" 
        gap={4} 
        maxW={{ base: "100%", md: "500px", lg: "600px" }}
        w="100%"

      >
        <Box w="100%">
          <FaceliftSelect
            placeholder="Select a tutorial"
            value={[selectedTutorial]}
            onValueChange={(value) => setSelectedTutorial(value.value[0])}
            options={labeledTutorials}
          />
        </Box>

        <FaceliftButton
          onClick={handleStartTutorial}
          disabled={!selectedTutorial || isTourActive || isTutorialDisabled}
          loading={isTourActive}
          size="md"
          width={{ base: "100%", md: "200px" }}
        >
          {isTourActive
            ? 'Tour in Progress...'
            : isTutorialDisabled
              ? 'Tutorial Disabled'
              : 'Start Tutorial'}
        </FaceliftButton>

        <Show when={isTutorialDisabled}>
          <Text 
            color="orange.500" 
            fontSize={{ base: "xs", md: "sm" }} 
            fontStyle="italic"
          >
            This tutorial is disabled and cannot be started.
          </Text>
        </Show>

        <Show when={availableTutorials.length === 0}>
          <Text 
            color="gray.500" 
            fontSize={{ base: "xs", md: "sm" }} 
            fontStyle="italic"
          >
            No tutorials available at the moment.
          </Text>
        </Show>
      </Flex>
    </Box>
  );
};

export default Tutorials;

